/**
 * Fetches a fresh CSRF token from Craft's session-info endpoint and
 * updates any existing hidden CSRF inputs in the page.
 *
 * Why we need this: this site runs Blitz (static page caching). Cached
 * HTML embeds a CSRF token that's stale by the time the visitor sees the
 * page, so any form submit (newsletter signup, etc.) gets rejected
 * with a 400. Running this on DOMContentLoaded fetches a fresh token and
 * patches the hidden inputs so the next submit succeeds.
 *
 * Emits a `csrf:refreshed` CustomEvent on document so downstream code
 * can react (e.g. AJAX consumers that hold a copy of the token).
 */
const refreshCsrfToken = {
    /** Resolved token: { name, value } or null */
    token: null,
    _promise: null,

    init() {
        // Deduplicate concurrent calls
        if (this._promise) return this._promise;

        this._promise = fetch('/actions/users/session-info', {
            headers: { 'Accept': 'application/json' },
        })
            .then((response) => response.json())
            .then((data) => {
                const tokenName = data.csrfTokenName;
                const tokenValue = data.csrfTokenValue;

                if (!tokenName || !tokenValue) {
                    console.error('refreshCsrfToken: no CSRF data in session-info response');
                    return;
                }

                this.token = { name: tokenName, value: tokenValue };

                // Update any existing hidden CSRF inputs
                const csrfInputs = document.querySelectorAll(`form input[name="${tokenName}"]`);
                csrfInputs.forEach((input) => {
                    input.value = tokenValue;
                });

                document.dispatchEvent(
                    new CustomEvent('csrf:refreshed', {
                        detail: { name: tokenName, value: tokenValue },
                    }),
                );
            })
            .catch((error) => {
                console.error('Error fetching CSRF token:', error);
            })
            .finally(() => {
                this._promise = null;
            });

        return this._promise;
    },

    /**
     * Returns the current token synchronously, or null if not yet fetched.
     * @returns {{ name: string, value: string } | null}
     */
    getToken() {
        return this.token;
    },

    /**
     * Returns a promise that resolves with the token.
     * If not yet fetched, triggers a fetch.
     * @returns {Promise<{ name: string, value: string } | null>}
     */
    async getTokenAsync() {
        if (this.token) return this.token;
        await this.init();
        return this.token;
    },
};

export default refreshCsrfToken;
